import { MobileRouteHandler } from 'MobileRouteHandler';
import { LazyLoader, Toast } from '@bp/ui-components';
import classNames from 'classnames';
import { Header } from 'components/Header/Header';
import { MobileMenu } from 'components/MobileMenu/MobileMenu';
import { PermissionCheckerProvider } from 'context/PermissionCheckerProvider';
import dayjs from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { Suspense, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { useQuery } from 'urql';
import { CONNECT_DATE_TEMPLATE } from 'utils/connectLib';
import styles from './App.module.scss';
import { graphql } from './client/gql';
import { PushNotificationWrapper } from './components/PushNotificationWrapper/PushNotificationWrapper';
import { Sidebar } from './components/Sidebar/Sidebar';
import { AppLayoutContext } from './context/AppLayoutContext';
import { DesktopContext } from './context/IsDesktop';
import { OrganizationConfigContextProvider } from './context/OrganizationConfigContext';
import { StackHandlerContextProvider } from './context/StackHandlerContext';
import { useRefetchTrigger } from './hooks/matrix/useRefetchTrigger';

const DataLoaderDataDocument = graphql(`
  query DataLoaderData(
    $notificationSettingsWhere: NotificationSettingsWhere!
    $profile: ID!
    $calendarEventWhere: CalendarEventWhere!
    $dayConnection: String!
    $organizationUuid: ID!
    $organizationUuids: [ID!]!
  ) {
    ...OrganizationConfigFields
    ...PermissionCheckerFields
    ...HeaderQueryFields
    ...MobileMenuQueryFields
    ...PushNotificationWrapperFields
    ...UseDMRoomsFields 
  }
`);

export function DataLoader() {
  const { pimAuthClaims } = useAuthClaims();
  const userUuid = pimAuthClaims.getUser().uuid;
  const profile = pimAuthClaims.getProfile();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [{ data }, refetch] = useQuery({
    query: DataLoaderDataDocument,
    variables: {
      organizationUuid,
      organizationUuids: [organizationUuid],
      dayConnection: dayjs().format(CONNECT_DATE_TEMPLATE),
      profileWhere: {
        organization: {
          uuid: pimAuthClaims.getOrganizationUuid(),
        },
      },
      notificationSettingsWhere: {
        userUuid,
      },
      profile: profile?.uuid ?? '',
      calendarEventWhere: {
        AND: [
          { categories_NOT_INCLUDES: 'lesson' },
          {
            OR: [
              {
                virtualLocations_SOME: {
                  running: true,
                },
              },
              {
                daysConnection_SOME: {
                  node: { date: dayjs().format(CONNECT_DATE_TEMPLATE) },
                },
              },
            ],
          },
          {
            OR: [
              {
                holderConnection: {
                  Organization: {
                    node: { uuid: pimAuthClaims.getOrganization().uuid },
                  },
                },
              },
              {
                attendees_SOME: {
                  uuid: profile.uuid,
                },
              },
              {
                owner: {
                  uuid: profile.uuid,
                },
              },
            ],
          },
        ],
      },
    },
  });

  const { sidebarIsOpen, sidebarIsFixed, scrollRef } = useContext(AppLayoutContext);
  const desktopContext = useContext(DesktopContext);

  const mainClasses = classNames(
    styles['app-container'],
    {
      [styles['sidebar-open']]: sidebarIsOpen,
      [styles['sidebar-fixed']]: sidebarIsFixed,
    },
    'has-scrollbar',
  );

  useRefetchTrigger({ refetch });

  const isDesktop = desktopContext.isDesktop;

  return (
    <OrganizationConfigContextProvider query={data}>
      <PermissionCheckerProvider query={data}>
        <StackHandlerContextProvider>
          {isDesktop && <Sidebar />}
          <PushNotificationWrapper query={data} onChange={refetch}>
            {isDesktop && <Header className={styles.header} query={data} />}
            <main className={mainClasses} ref={scrollRef}>
              <Suspense fallback={<LazyLoader embedded transparent />}>
                <MobileRouteHandler />
                <Outlet />
              </Suspense>
            </main>
            {!isDesktop && <MobileMenu className={styles['mobile-menu']} query={data} />}
            <Toast />
          </PushNotificationWrapper>
        </StackHandlerContextProvider>
      </PermissionCheckerProvider>
    </OrganizationConfigContextProvider>
  );
}
